import { Serialised } from '@squaredup/ids';
import { MatchSpec } from '@squaredup/utilities';

type matchFunction = (sourceType: string) => boolean;

/**
 * Given a particular Data stream match spec, generate a function suitable for determining if a given
 * string would match the spec purely from a sourceType perspective
 * @param matchSpec Matches property of a particular datastream
 * @returns function that will return true if provided source type would match the spec
 */
export const getSourceTypesFilter = (matchSpec: Serialised<MatchSpec> | undefined): matchFunction => {
    // All, None, etc are the same for our purpose, which is a filter where we can't reduce by source type
    if (!matchSpec || typeof matchSpec === 'string') {
        return () => true;
    }

    const matches = Array.isArray(matchSpec) ? matchSpec : [matchSpec];

    const matchFunctions = matches.flatMap((match) =>
        Object.entries(match)
            // only interested in sourceType filters, taking into account legacy suffix
            .filter(([prop, _]) => prop.replace(/.0$/u, '') === 'sourceType')
            .flatMap(([_, criteria]) => {
                if (typeof criteria === 'string') {
                    return (sourceType: string) => sourceType === criteria;
                }

                switch (criteria.type) {
                    case 'oneOf':
                        return (sourceType: string) => criteria.values.includes(sourceType);
                    case 'equals':
                        return (sourceType: string) => sourceType === criteria.value;
                    case 'contains':
                        return (sourceType: string) => sourceType.includes(sourceType);
                    case 'regex':
                        return (sourceType: string) => sourceType.match(sourceType) !== null;
                    default:
                        // All other cases we can't directly translate the match spec
                        return () => true;
                }
            })
    );

    return (sourceType: string) => (matchFunctions.length ? matchFunctions.some((f) => f(sourceType)) : true);
};
